import React, { useState } from 'react';
import { Button, Icon, Intent, Text } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { ZipApi } from '../../../lib/ZipApi';
import { ProductInstance } from '../../../types/DataSyncItem';
import { showToasts } from '../../../lib/toaster';
import { AppDetailStore } from '../../../stores/AppDetailStore';
import styles from './CMSUIExtensions.module.sass';

interface Props {
  instance: ProductInstance;
  isEnabled: boolean;
  detailStore: AppDetailStore;
  onInstanceUpdated: (instanceId: string, isEnabled: boolean) => void;
}

export const CMSUIExtensionRow = ({ instance, isEnabled, detailStore, onInstanceUpdated }: Props) => {
  const [isOperationLoading, setIsOperationLoading] = useState(false);

  const handleToggleExtension = async () => {
    if (!detailStore.installId || isOperationLoading) {
      return;
    }

    setIsOperationLoading(true);

    try {
      if (isEnabled) {
        await ZipApi.disableCMSUIExtension(
          detailStore.installId,
          instance.id
        );
        showToasts([{intent: 'success', message: 'CMS UI extensions disabled successfully'}]);
        onInstanceUpdated(instance.id, false);
      } else {
        await ZipApi.enableCMSUIExtension(
          detailStore.installId,
          instance.id
        );
        showToasts([{intent: 'success', message: 'CMS UI extensions enabled successfully'}]);
        onInstanceUpdated(instance.id, true);
      }
    } catch (error) {
      showToasts([{intent: Intent.DANGER, message: 'Error updating CMS UI extensions'}]);
    } finally {
      setIsOperationLoading(false);
    }
  };

  const getStatusIcon = () => {
    return (
      <Icon
        icon={isEnabled ? IconNames.TICK_CIRCLE : IconNames.PAUSE}
        intent={isEnabled ? Intent.SUCCESS : Intent.NONE}
        className={styles.statusIcon}
      />
    );
  };

  const getActionButtons = () => {
    return (
      <div className={styles.actionButtons}>
        <Button
          text={isEnabled ? 'Remove Extensions' : 'Add Extensions'}
          intent={isEnabled ? Intent.WARNING : Intent.SUCCESS}
          icon={isEnabled ? IconNames.STOP : IconNames.PLAY}
          onClick={handleToggleExtension}
          small
          loading={isOperationLoading}
          disabled={isOperationLoading}
        />
      </div>
    );
  };

  return (
    <tr key={instance.id}>
      <td>
        <div className={styles.instanceName}>
          {getStatusIcon()}
          <Text>{instance.name}</Text>
        </div>
      </td>
      <td>
        {getActionButtons()}
      </td>
    </tr>
  );
};
